import { presentations as presentationsApi } from "apis/callables";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import * as Thumbnails from "js/core/models/thumbnails";
import * as appSearch from "js/core/services/appSearch";

import { THUMBNAIL_SIZES } from "common/constants";
import DataService from "./DataService";

interface UserSlidesDataServiceSearchQuery {
    fullTextSearchQuery?: string,
    fullTextSearchByFields?: string[],
    size?: number,
    minScore?: number,
    presentationId?: string
    workspaceId?: string
}

interface UserSlidesDataServiceDataItem {
    id: string,
    index: number,
    presentationId: string,
    presentationName: string,
    workspaceId: string,
    getThumbnailUrl: () => Promise<string>,
    score: number
}

class UserSlidesDataService extends DataService {
    public async search(query: UserSlidesDataServiceSearchQuery): Promise<UserSlidesDataServiceDataItem[]> {
        const {
            fullTextSearchQuery,
            fullTextSearchByFields = [],
            size = UserSlidesDataService.SEARCH_QUERY_SIZE,
            minScore = UserSlidesDataService.SEARCH_QUERY_MIN_SCORE,
            presentationId,
            workspaceId
        } = query;

        let searchFields = undefined;
        if (fullTextSearchByFields.length) {
            searchFields = fullTextSearchByFields.reduce((searchFields, fieldName) => {
                return {
                    ...searchFields,
                    [fieldName]: {},
                };
            }, {});
        }

        let { results: appsearchResults } = await appSearch.search({
            workspaceId,
            searchEngine: appSearch.SearchEngine.USER_SLIDES,
            query: fullTextSearchQuery ?? "",
            // x3 is a rough estimation to mitigate the subsequent filtering by slides not being soft deleted
            // (we don't want to always use UserSlidesDataService.SEARCH_QUERY_SIZE because we'd have to load
            // too many presentations which is expensive)
            page: { size: Math.min(UserSlidesDataService.SEARCH_QUERY_SIZE, size * 3) },
            filters: {
                presentation_id: presentationId ? [presentationId] : undefined,
                org_id: (!presentationId && workspaceId) ? [workspaceId] : undefined
            },
            searchFields,
        });

        appsearchResults = appsearchResults
            // Filter by score to avoid excessive presentations loading
            .filter(result => result._meta.score >= minScore);

        const presentationIds = [...new Set(appsearchResults.map(result => result.presentation_id.raw))];
        const presentationsMetadata = await presentationsApi.getPresentationsMetadata({ presentationIds, workspaceId });

        let searchResults: UserSlidesDataServiceDataItem[] = await Promise.all(appsearchResults.map(async ({
            slide_id: { raw: slideId },
            presentation_id: { raw: presentationId },
            modified_at,
            _meta: { score }
        }): Promise<UserSlidesDataServiceDataItem | null> => {
            const metadata = presentationsMetadata.find(m => m.id === presentationId);
            if (!metadata) {
                return null;
            }

            const slideIndex = metadata.slideIds.indexOf(slideId);
            if (slideIndex < 0) {
                return null;
            }

            const modifiedAt = modified_at?.raw;
            return {
                id: slideId,
                index: slideIndex,
                presentationId: presentationId,
                presentationName: metadata.name,
                workspaceId: metadata.workspaceId,
                getThumbnailUrl: (showThemeThumbnails = false) => {
                    if (showThemeThumbnails && ds.selection.presentation && !ds.selection.presentation.get("isDummy")) {
                        const presentationId = ds.selection.presentation.id;
                        const firstSlideModifiedAt = ds.selection.presentation.get("firstSlideModifiedAt");
                        return Thumbnails.getSignedUrlAndLoad(slideId, firstSlideModifiedAt, presentationId, THUMBNAIL_SIZES["small"].suffix);
                    }

                    const slide = new Slide({ id: slideId }, { autoLoad: false });

                    if (modifiedAt) {
                        // No need to load slide model
                        return slide.getThumbnailUrl(presentationId, "small", 0, true, modifiedAt);
                    } else {
                        return slide.load().then(() => slide.getThumbnailUrl(presentationId));
                    }
                },
                score
            };
        }));

        searchResults = searchResults
            // Remove non existing slides
            .filter(slide => !!slide)
            // Respect size
            .slice(0, size);

        if (presentationId) {
            // Sort by slide index
            searchResults = searchResults
                .sort((a, b) => a.index - b.index);
        } else {
            // Sort by score desc
            searchResults = searchResults
                .sort((a, b) => b.score - a.score);
        }

        return searchResults;
    }
}

export default UserSlidesDataService;
